/**
 * FMD Session Manager
 * Gère les données FMD stockées en localStorage et remplace dynamiquement
 * les valeurs dans les spans avec data-fmd-* attributes
 */
(function () {
  const FMD_STORAGE_KEY = 'fmd-data';
  const FMD_METAOBJECT_KEY = 'fmd';
  const FMD_GENERIC_FLOW_KEY = 'fmd-generic-flow';
  // TTL : 7 jours en millisecondes
  const FMD_DATA_TTL = 7 * 24 * 60 * 60 * 1000;

  class FmdSession {
    constructor() {
      // Écouter les changements de storage d'autres onglets
      this.setupStorageListener();

      // Vérifie si on a un paramètre fmd dans l'URL (metaobject/landing partenaire)
      this.checkMetaobjectUrl();

      // Vérifie si on est dans le parcours FMD générique (via view=parcours-fmd)
      this.checkGenericFlowUrl();

      // Nettoie view=location si on est dans un parcours FMD
      this.cleanLocationViewParam();

      this.data = this.getData();
      if (this.data) {
        this.replaceValues();
        this.updateNavLinks();
        this.interceptProductLinks();
        this.addFmdGeneriqueClass();
        this.interceptAddToCart();
        this.injectFmdHandle();
        this.observeProductLinks();
      }

      // Toujours attacher le handler de sortie générique (même sans data),
      // car le logo peut avoir data-fmd-generic-exit sur /pages/simulation-fmd
      this.bindGenericExitLinks();
    }

    /**
     * Attache un handler de clic sur tous les éléments [data-fmd-generic-exit].
     * Nettoie localStorage et les params FMD de l'URL avant de naviguer.
     * Fonctionne indépendamment de <fmd-form>.
     */
    bindGenericExitLinks() {
      document.querySelectorAll('[data-fmd-generic-exit]').forEach((el) => {
        el.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          // Nettoyer toutes les données FMD du localStorage
          try {
            localStorage.removeItem(FMD_GENERIC_FLOW_KEY);
            localStorage.removeItem(FMD_STORAGE_KEY);
            localStorage.removeItem(FMD_METAOBJECT_KEY);
          } catch (e) {
            console.error('FMD Session: Error clearing localStorage on exit', e);
          }

          // Résoudre l'URL cible et nettoyer les params FMD
          try {
            const link = event.currentTarget.closest('a');
            let targetUrl =
              link && link.getAttribute('href')
                ? new URL(link.href, window.location.origin)
                : new URL(window.location.href);

            targetUrl.searchParams.delete('fmd-generic');
            targetUrl.searchParams.delete('fmd');
            if (targetUrl.searchParams.get('view') === 'parcours-fmd') {
              targetUrl.searchParams.delete('view');
            }

            window.location.replace(targetUrl.toString());
          } catch (e) {
            // Fallback : naviguer vers l'accueil
            window.location.replace('/');
          }
        });
      });
    }

    /**
     * Écoute les changements de localStorage depuis d'autres onglets
     * et redirige vers le parcours entreprise si un handle FMD est défini
     */
    setupStorageListener() {
      window.addEventListener('storage', (e) => {
        if (![FMD_STORAGE_KEY, FMD_METAOBJECT_KEY, FMD_GENERIC_FLOW_KEY].includes(e.key)) {
          return;
        }

        // Cas 1: Une clé FMD a été SUPPRIMÉE (sortie du parcours FMD)
        // e.newValue === null signifie que la clé a été supprimée via removeItem()
        if (e.newValue === null) {
          // Si un handle partenaire est actif et que la clé supprimée n'est pas fmd,
          // c'est une transition générique→partenaire, pas une sortie
          if (e.key !== FMD_METAOBJECT_KEY) {
            try {
              if (localStorage.getItem(FMD_METAOBJECT_KEY) !== null) return;
            } catch (err) {}
          }
          // Nettoyage local des clés restantes.
          // Le guard getItem() évite des removeItem() inutiles qui
          // déclencheraient des événements storage en cascade.
          try {
            if (localStorage.getItem(FMD_STORAGE_KEY) !== null) localStorage.removeItem(FMD_STORAGE_KEY);
            if (localStorage.getItem(FMD_METAOBJECT_KEY) !== null) localStorage.removeItem(FMD_METAOBJECT_KEY);
            if (localStorage.getItem(FMD_GENERIC_FLOW_KEY) !== null) localStorage.removeItem(FMD_GENERIC_FLOW_KEY);
          } catch (err) {}

          // Rediriger vers la homepage (sortie du parcours FMD)
          window.location.href = '/';
          return;
        }

        // Cas 2: Une clé FMD a été MODIFIÉE ou AJOUTÉE
        try {
          const currentUrl = new URL(window.location.href);
          const normalizedPath = currentUrl.pathname.replace(/\/$/, '');
          const isFmdGenericPage = normalizedPath === '/pages/simulation-fmd';
          const hasGenericParam = currentUrl.searchParams.has('fmd-generic');

          if (e.key === FMD_METAOBJECT_KEY) {
            const handle = localStorage.getItem(FMD_METAOBJECT_KEY);
            if (handle && (isFmdGenericPage || hasGenericParam)) {
              if (localStorage.getItem(FMD_GENERIC_FLOW_KEY) !== null) localStorage.removeItem(FMD_GENERIC_FLOW_KEY);
              const partnerUrl = '/pages/partners/' + encodeURIComponent(handle) + '?fmd=' + encodeURIComponent(handle);
              window.location.replace(partnerUrl);
              return;
            }
          }
        } catch (err) {}

        // Fallback : recharger pour synchroniser
        window.location.reload();
      });
    }

    /**
     * Vérifie si l'URL contient un paramètre fmd (metaobject)
     * Si oui, supprime fmd-data et stocke les données du metaobject dans fmd
     */
    checkMetaobjectUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      var currentUrl = new URL(window.location.href);
      const fmdHandle = urlParams.get('fmd');

      if (fmdHandle) {
        // Priorité parcours entreprise : supprimer les données génériques
        localStorage.removeItem(FMD_STORAGE_KEY);
        localStorage.removeItem(FMD_GENERIC_FLOW_KEY);

        var needsRedirect = false;

        if (currentUrl.searchParams.has('fmd-generic')) {
          currentUrl.searchParams.delete('fmd-generic');
          needsRedirect = true;
        }

        if (needsRedirect) {
          window.location.replace(currentUrl.toString());
        }

        // Récupère les données du metaobject depuis le script JSON
        const metaobjectScript = document.querySelector('script[data-fmd-metaobject]');
        if (metaobjectScript) {
          try {
            const metaobjectData = JSON.parse(metaobjectScript.textContent);
            // Stocke uniquement le handle dans 'fmd' pour les URLs
            if (metaobjectData.handle) {
              localStorage.setItem(FMD_METAOBJECT_KEY, metaobjectData.handle);
            }
          } catch (e) {
            console.error('FMD Session: Error parsing metaobject data', e);
          }
        }
      }
    }

    /**
     * Vérifie si l'URL contient view=parcours-fmd (parcours FMD générique)
     * Si oui, stocke l'info dans localStorage pour maintenir le contexte FMD
     */
    checkGenericFlowUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const viewParam = urlParams.get('view');

      // Si on est sur une page avec view=parcours-fmd, on est dans le parcours générique
      if (viewParam === 'parcours-fmd' && urlParams.get('fmd') === null) {
        try {
          localStorage.setItem(FMD_GENERIC_FLOW_KEY, 'true');
        } catch (e) {
          console.error('FMD Session: Error storing generic flow in localStorage', e);
        }
      }
    }

    /**
     * Nettoie le paramètre view=location de l'URL si on est dans un parcours FMD.
     * Cela évite le conflit entre le template location et le parcours FMD.
     * Redirige vers l'URL nettoyée avec view=parcours-fmd.
     */
    cleanLocationViewParam() {
      try {
        const currentUrl = new URL(window.location.href);
        const viewParam = currentUrl.searchParams.get('view');

        // On ne traite que view=location
        if (viewParam !== 'location') {
          return;
        }

        // Vérifie si on est dans un parcours FMD (partenaire ou générique)
        const isFmdPartner = currentUrl.searchParams.has('fmd') || localStorage.getItem(FMD_METAOBJECT_KEY);
        const isFmdGeneric =
          currentUrl.searchParams.has('fmd-generic') || localStorage.getItem(FMD_GENERIC_FLOW_KEY) === 'true';

        if (isFmdPartner || isFmdGeneric) {
          // Remplace view=location par view=parcours-fmd
          currentUrl.searchParams.set('view', 'parcours-fmd');

          // Ajoute fmd-generic=1 si on est dans le parcours générique et qu'il n'est pas déjà présent
          if (isFmdGeneric && !currentUrl.searchParams.has('fmd-generic')) {
            currentUrl.searchParams.set('fmd-generic', '1');
          }

          console.info('FMD Session: Cleaned view=location, redirecting to FMD view');
          window.location.replace(currentUrl.toString());
        }
      } catch (e) {
        console.error('FMD Session: Error cleaning location view param', e);
      }
    }

    /**
     * Vérifie si on est dans le parcours FMD générique
     * @returns {boolean} True si on est dans le parcours FMD générique
     */
    static isGenericFlow() {
      try {
        return localStorage.getItem(FMD_GENERIC_FLOW_KEY) === 'true';
      } catch (e) {
        return false;
      }
    }

    /**
     * Récupère le handle du metaobject depuis localStorage
     * @returns {string|null} Le handle du metaobject ou null
     */
    static getMetaobjectHandle() {
      try {
        return localStorage.getItem(FMD_METAOBJECT_KEY) || null;
      } catch (e) {
        console.error('FMD Session: Error reading metaobject handle from localStorage', e);
        return null;
      }
    }

    /**
     * Récupère les données FMD depuis localStorage avec vérification d'expiration
     * @returns {Object|null} Les données FMD ou null si non présentes ou expirées
     */
    getData() {
      try {
        const stored = localStorage.getItem(FMD_STORAGE_KEY);
        if (!stored) return null;

        const parsed = JSON.parse(stored);

        if (parsed && parsed.timestamp && parsed.expiresIn) {
          const isExpired = Date.now() - parsed.timestamp > parsed.expiresIn;
          if (isExpired) {
            FmdSession.clearAllData();
            console.info('FMD Session: Data expired, cleared all FMD keys');
            return null;
          }
          return parsed.data || null;
        }

        let legacyData = null;
        if (parsed && parsed.data) {
          legacyData = parsed.data;
        } else if (parsed && typeof parsed === 'object') {
          legacyData = parsed;
        }

        if (legacyData) {
          const migrated = {
            data: legacyData,
            timestamp: Date.now(),
            expiresIn: FMD_DATA_TTL,
          };
          localStorage.setItem(FMD_STORAGE_KEY, JSON.stringify(migrated));
          return legacyData;
        }

        FmdSession.clearData();
        return null;
      } catch (e) {
        console.error('FMD Session: Error reading localStorage', e);
        return null;
      }
    }

    /**
     * Stocke les données FMD dans localStorage
     * @param {Object} data - Les données à stocker
     */
    static setData(data) {
      try {
        const payload = {
          data: data,
          timestamp: Date.now(),
          expiresIn: FMD_DATA_TTL,
        };
        localStorage.setItem(FMD_STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        console.error('FMD Session: Error writing to localStorage', e);
      }
    }

    /**
     * Efface les données FMD du localStorage
     */
    static clearData() {
      try {
        localStorage.removeItem(FMD_STORAGE_KEY);
      } catch (e) {
        console.error('FMD Session: Error clearing localStorage', e);
      }
    }

    /**
     * Efface TOUTES les données FMD du localStorage
     * Utilisé lors de l'expiration ou de la sortie explicite du parcours FMD
     */
    static clearAllData() {
      try {
        if (localStorage.getItem(FMD_STORAGE_KEY) !== null) localStorage.removeItem(FMD_STORAGE_KEY);
        if (localStorage.getItem(FMD_GENERIC_FLOW_KEY) !== null) localStorage.removeItem(FMD_GENERIC_FLOW_KEY);
        if (localStorage.getItem(FMD_METAOBJECT_KEY) !== null) localStorage.removeItem(FMD_METAOBJECT_KEY);
      } catch (e) {
        console.error('FMD Session: Error clearing all FMD data from localStorage', e);
      }
    }

    /**
     * Remplace les valeurs dans tous les spans avec data-fmd-* attributes
     */
    replaceValues() {
      // Remplace [amount] / data-fmd-amount
      if (this.data.amount) {
        let amount = this.data.amount;
        // Ajoute le symbole € si pas déjà présent
        if (!amount.includes('€')) {
          amount = amount + '€';
        }
        document.querySelectorAll('[data-fmd-amount]').forEach((el) => {
          el.textContent = amount;
        });
      }

      // Remplace [name] / data-fmd-name
      if (this.data.name) {
        document.querySelectorAll('[data-fmd-name]').forEach((el) => {
          el.textContent = this.data.name;
        });
      }

      // Remplace [firstname] / data-fmd-firstname (si nécessaire)
      if (this.data.firstname) {
        document.querySelectorAll('[data-fmd-firstname]').forEach((el) => {
          el.textContent = this.data.firstname;
        });
      }

      // Remplace [lastname] / data-fmd-lastname (si nécessaire)
      if (this.data.lastname) {
        document.querySelectorAll('[data-fmd-lastname]').forEach((el) => {
          el.textContent = this.data.lastname;
        });
      }

      // Calcule et remplace le prix final FMD (prix - montant FMD)
      // et le montant mensuel (montant FMD ÷ 12)
      if (this.data.amount) {
        // Extrait le nombre du montant (ex: "500€" -> 500)
        const amountMatch = this.data.amount.match(/[\d,.]+/);
        if (amountMatch) {
          const fmdAmount = parseFloat(amountMatch[0].replace(',', '.'));

          // Calcul du montant mensuel FMD (montant ÷ 12)
          const monthlyAmount = Math.round(fmdAmount / 12);
          const monthlyText = monthlyAmount + '€';

          // Remplace data-fmd-monthly
          document.querySelectorAll('[data-fmd-monthly]').forEach((el) => {
            el.textContent = monthlyText;
          });

          // Remplace data-fmd-amount-per-month (même valeur)
          document.querySelectorAll('[data-fmd-amount-per-month]').forEach((el) => {
            el.textContent = monthlyText;
          });

          // Remplace data-fmd-final-price (prix produit - montant FMD)
          document.querySelectorAll('[data-fmd-final-price]').forEach((el) => {
            const priceValue = el.dataset.productPriceValue;
            if (priceValue) {
              // Prix en centimes depuis le data attribute
              const productPrice = parseInt(priceValue, 10) / 100;
              const finalPrice = Math.max(0, productPrice - fmdAmount);
              el.textContent = new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              }).format(finalPrice);
            }
          });
          // Remplace data-fmd-monthly-price (prix mensuel)
          document.querySelectorAll('[data-fmd-monthly-price]').forEach((el) => {
            const priceValue = el.dataset.productPriceValue;
            if (priceValue) {
              const productPrice = parseInt(priceValue, 10) / 100;
              const finalPrice = Math.max(0, productPrice - fmdAmount);
              const monthlyPrice = Math.round(finalPrice / 12);
              el.textContent = monthlyPrice + ' €';
            }
          });
        }
      }
    }

    /**
     * Met à jour les liens de navigation pour rediriger vers la page FMD générique
     * quand l'utilisateur vient du formulaire (localStorage présent)
     */
    updateNavLinks() {
      const fmdPageUrl = '/pages/simulation-fmd';
      const fmdCollectionUrl = '/collections/tous-nos-velos-reconditionnes-fmd';

      // Met à jour le lien du logo (sauf si c'est un CTA de sortie)
      const logoLink = document.querySelector('.site-header__logo-link');
      if (logoLink && !logoLink.hasAttribute('data-fmd-generic-exit')) {
        logoLink.href = fmdPageUrl;
      }

      // Met à jour les liens mobile nav
      document.querySelectorAll('.mobile-nav__link--top-level').forEach((link) => {
        const text = link.textContent.trim().toLowerCase();
        if (
          text === 'le fonctionnement' ||
          text === 'accueil' ||
          link.href === '/' ||
          link.getAttribute('href') === '/'
        ) {
          link.href = fmdPageUrl;
        }
      });

      // Met à jour les liens breadcrumb vers la racine pour rediriger vers la collection FMD
      document.querySelectorAll('.breadcrumb a').forEach((link) => {
        const href = link.getAttribute('href');
        if (href === '/' || href === '') {
          link.href = fmdCollectionUrl;
        }
      });
    }

    /**
     * Ajoute la classe fmd-generique sur .collection-fmd__intro-text
     * quand l'utilisateur vient du parcours FMD générique (formulaire)
     */
    addFmdGeneriqueClass() {
      const introText = document.querySelector('.collection-fmd__intro-text');
      if (introText && !introText.classList.contains('fmd-generique')) {
        introText.classList.add('fmd-generique');
      }
    }

    /**
     * Convertit une chaîne en format "handle" (slug)
     * @param {string} str - La chaîne à convertir
     * @returns {string} La chaîne en format handle
     */
    static handleize(str) {
      if (!str) return '';
      return str
        .toString()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Supprime les accents
        .replace(/[^a-z0-9]+/g, '-') // Remplace les caractères non alphanumériques par des tirets
        .replace(/^-+|-+$/g, '') // Supprime les tirets en début et fin
        .replace(/-+/g, '-'); // Remplace les tirets multiples par un seul
    }

    /**
     * Injecte le handle FMD (nom de l'entreprise handleizé) dans les boutons d'ajout au panier
     * sur le parcours FMD générique quand l'attribut is-fmd est vide
     */
    injectFmdHandle() {
      if (!this.data.name) return;

      const handle = FmdSession.handleize(this.data.name);
      if (!handle) return;

      // Injecte le handle dans les boutons avec is-fmd vide
      document.querySelectorAll('[data-add-to-cart][is-fmd]').forEach((btn) => {
        const currentValue = btn.getAttribute('is-fmd');
        if (!currentValue || currentValue.trim() === '') {
          btn.setAttribute('is-fmd', handle);
        }
      });
    }

    /**
     * Ajoute view=parcours-fmd aux liens produits déjà présents dans le DOM au chargement.
     * Les liens ajoutés dynamiquement sont traités par observeProductLinks().
     */
    interceptProductLinks() {
      document.querySelectorAll('a[href*="/products"]').forEach((link) => {
        const href = link.getAttribute('href');
        if (href && !href.includes('view=')) {
          link.href = this.addViewParam(href);
        }
      });
    }

    /**
     * Ajoute le paramètre view=parcours-fmd à une URL.
     * Remplace view=location si présent pour éviter le conflit de templates.
     * @param {string} url - L'URL à modifier
     * @returns {string} L'URL avec le paramètre ajouté ou remplacé
     */
    addViewParam(url) {
      try {
        const urlObj = new URL(url, window.location.origin);
        const currentView = urlObj.searchParams.get('view');

        // Remplace view=location ou ajoute view=parcours-fmd si absent
        if (!currentView || currentView === 'location') {
          urlObj.searchParams.set('view', 'parcours-fmd');
        }

        return urlObj.pathname + urlObj.search + urlObj.hash;
      } catch (e) {
        // Fallback pour les URLs relatives simples
        // Remplace view=location si présent
        if (url.includes('view=location')) {
          return url.replace('view=location', 'view=parcours-fmd');
        }
        if (url.includes('?')) {
          return url + '&view=parcours-fmd';
        }
        return url + '?view=parcours-fmd';
      }
    }

    /**
     * Intercepte l'ajout au panier pour rediriger vers /cart dans le parcours FMD générique
     * Cela permet de conserver le contexte FMD (localStorage) après l'ajout
     */
    interceptAddToCart() {
      // Vérifier qu'on n'est pas déjà dans le parcours FMD classique (avec ?fmd= dans l'URL)
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has('fmd')) return;

      // Intercepter les formulaires d'ajout au panier
      const forms = document.querySelectorAll('form[action*="/cart/add"]');

      forms.forEach((form) => {
        // Éviter d'intercepter plusieurs fois le même formulaire
        if (form.dataset.fmdIntercepted) return;

        const addToCartBtn = form.querySelector('[data-add-to-cart]');
        if (!addToCartBtn) return;

        // Vérifier que le bouton n'a pas de handle FMD (parcours classique)
        const fmdHandle = addToCartBtn.getAttribute('is-fmd');
        if (fmdHandle && fmdHandle.trim() !== '') return;

        // Marquer le formulaire comme intercepté
        form.dataset.fmdIntercepted = 'true';

        // Intercepter la soumission du formulaire
        form.addEventListener('submit', async (evt) => {
          evt.preventDefault();
          evt.stopImmediatePropagation();

          addToCartBtn.disabled = true;
          addToCartBtn.classList.add('btn--loading');

          try {
            const formData = new FormData(form);
            // Ajouter la propriété _isFmd pour identifier le parcours FMD
            formData.append('properties[_isFmd]', 'true');

            const response = await fetch('/cart/add.js', {
              method: 'POST',
              body: formData,
            });

            const data = await response.json();

            if (data.status === 422) {
              // Erreur (produit indisponible, etc.)
              addToCartBtn.disabled = false;
              addToCartBtn.classList.remove('btn--loading');
            } else {
              // Succès - synchroniser le marquage puis rediriger
              const syncMarquage = window.theme?.cart?.syncMarquage;
              if (typeof syncMarquage === 'function') {
                syncMarquage()
                  .catch(() => {})
                  .then(() => {
                    window.location.href = '/cart?fmd-generic=1';
                  });
              } else {
                window.location.href = '/cart?fmd-generic=1';
              }
            }
          } catch (error) {
            addToCartBtn.disabled = false;
            addToCartBtn.classList.remove('btn--loading');
          }
        });
      });
    }

    /**
     * Observe le DOM pour intercepter les liens produits ajoutés dynamiquement
     * et réappliquer les modifications FMD quand le DOM est remplacé (filtrage)
     */
    observeProductLinks() {
      const observer = new MutationObserver((mutations) => {
        let shouldReapplyFmd = false;
        let shouldReinterceptForms = false;

        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Vérifie si le nœud ajouté est un lien produit
              if (node.tagName === 'A' && node.href && node.href.includes('/products')) {
                const href = node.getAttribute('href');
                if (href && !href.includes('view=')) {
                  node.href = this.addViewParam(href);
                }
              }
              // Vérifie les liens produits à l'intérieur du nœud ajouté
              node.querySelectorAll?.('a[href*="/products"]')?.forEach((link) => {
                const href = link.getAttribute('href');
                if (href && !href.includes('view=')) {
                  link.href = this.addViewParam(href);
                }
              });

              // Vérifie si .collection-fmd__intro-text a été réinjecté
              if (
                node.classList?.contains('collection-fmd__intro-text') ||
                node.querySelector?.('.collection-fmd__intro-text')
              ) {
                shouldReapplyFmd = true;
              }

              // Vérifie si un formulaire d'ajout au panier a été ajouté
              if (
                (node.tagName === 'FORM' && node.action?.includes('/cart/add')) ||
                node.querySelector?.('form[action*="/cart/add"]')
              ) {
                shouldReinterceptForms = true;
              }
            }
          });
        });

        // Réapplique les modifications FMD si le DOM a été remplacé
        if (shouldReapplyFmd) {
          this.addFmdGeneriqueClass();
          this.replaceValues();
        }

        // Réintercepter les formulaires d'ajout au panier si nécessaire
        if (shouldReinterceptForms) {
          this.interceptAddToCart();
          this.injectFmdHandle();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    }
  }

  // Expose la classe globalement pour usage externe
  window.FmdSession = FmdSession;

  // Auto-init au chargement du DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new FmdSession());
  } else {
    new FmdSession();
  }
})();
